import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTitle } from '@/lib/useTitle';
import DimensionLine from '@/components/DimensionLine';

export const meta = {
  path: '/commercial-lease-nnn-rent-calculator',
  title: 'Commercial Lease & NNN Rent Calculator — Waterloo Region & Guelph',
  description:
    'Calculate commercial base rent, TMI/additional rent, monthly occupancy cost and effective term cost for office, retail and industrial space in Waterloo Region and Guelph.',
};

function money(n: number) {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);
}

export default function CommercialLeaseCalculator() {
  useTitle(meta.title, meta.description);

  const [area, setArea] = useState(3000);
  const [baseRent, setBaseRent] = useState(18);
  const [additionalRent, setAdditionalRent] = useState(12);
  const [termYears, setTermYears] = useState(5);
  const [freeBaseMonths, setFreeBaseMonths] = useState(3);

  const result = useMemo(() => {
    if (
      !Number.isFinite(area) || area <= 0 ||
      !Number.isFinite(baseRent) || baseRent < 0 ||
      !Number.isFinite(additionalRent) || additionalRent < 0 ||
      !Number.isFinite(termYears) || termYears <= 0 ||
      !Number.isFinite(freeBaseMonths) || freeBaseMonths < 0
    ) return null;

    const annualBase = area * baseRent;
    const annualAdditional = area * additionalRent;
    const monthlyBase = annualBase / 12;
    const monthlyAdditional = annualAdditional / 12;
    const monthlyOccupancy = monthlyBase + monthlyAdditional;
    const annualOccupancy = annualBase + annualAdditional;
    const totalMonths = Math.round(termYears * 12);
    const freeMonthsApplied = Math.min(Math.round(freeBaseMonths), totalMonths);
    const termBase = monthlyBase * (totalMonths - freeMonthsApplied);
    const termAdditional = monthlyAdditional * totalMonths;
    const totalTerm = termBase + termAdditional;
    const effectiveMonthly = totalTerm / totalMonths;
    const effectivePerSfYear = (effectiveMonthly * 12) / area;

    return {
      annualBase,
      annualAdditional,
      monthlyBase,
      monthlyAdditional,
      monthlyOccupancy,
      annualOccupancy,
      totalMonths,
      freeMonthsApplied,
      totalTerm,
      effectiveMonthly,
      effectivePerSfYear,
    };
  }, [area, baseRent, additionalRent, termYears, freeBaseMonths]);

  return (
    <>
      <section className="border-b border-medline bg-grid-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
          <p className="annot">Owner & tenant tool — lease economics</p>
          <h1 className="mt-4 max-w-4xl font-display text-[38px] font-semibold leading-[1.03] tracking-tight sm:text-[58px]">
            Commercial lease & NNN rent calculator.
          </h1>
          <p className="mt-6 max-w-3xl text-[17px] leading-relaxed text-annot">
            Convert quoted rent per square foot into monthly and annual occupancy cost. Model net/base rent, TMI or additional rent, term length and base-rent-free months without hiding the math.
          </p>
        </div>
      </section>

      <section className="bg-sheet">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[1fr_1fr]">
          <div>
            <DimensionLine label="Lease inputs" className="mb-8" />

            <div className="grid gap-6 sm:grid-cols-2">
              <label className="block">
                <span className="annot">Rentable area</span>
                <div className="mt-2 flex items-center border border-medline bg-white">
                  <input type="number" min="1" step="100" value={area} onChange={(e) => setArea(Number(e.target.value))} className="w-full bg-transparent px-4 py-4 text-[18px] outline-none" />
                  <span className="px-4 font-mono text-[13px] text-annot">sf</span>
                </div>
              </label>

              <label className="block">
                <span className="annot">Net / base rent</span>
                <div className="mt-2 flex items-center border border-medline bg-white">
                  <span className="px-4 font-mono text-[13px] text-annot">$</span>
                  <input type="number" min="0" step="0.25" value={baseRent} onChange={(e) => setBaseRent(Number(e.target.value))} className="w-full bg-transparent px-2 py-4 text-[18px] outline-none" />
                  <span className="px-4 font-mono text-[12px] text-annot">/sf/yr</span>
                </div>
              </label>

              <label className="block">
                <span className="annot">TMI / additional rent</span>
                <div className="mt-2 flex items-center border border-medline bg-white">
                  <span className="px-4 font-mono text-[13px] text-annot">$</span>
                  <input type="number" min="0" step="0.25" value={additionalRent} onChange={(e) => setAdditionalRent(Number(e.target.value))} className="w-full bg-transparent px-2 py-4 text-[18px] outline-none" />
                  <span className="px-4 font-mono text-[12px] text-annot">/sf/yr</span>
                </div>
              </label>

              <label className="block">
                <span className="annot">Lease term</span>
                <div className="mt-2 flex items-center border border-medline bg-white">
                  <input type="number" min="0.25" step="0.25" value={termYears} onChange={(e) => setTermYears(Number(e.target.value))} className="w-full bg-transparent px-4 py-4 text-[18px] outline-none" />
                  <span className="px-4 font-mono text-[13px] text-annot">years</span>
                </div>
              </label>

              <label className="block sm:col-span-2">
                <span className="annot">Base-rent-free months — optional</span>
                <div className="mt-2 flex items-center border border-medline bg-white">
                  <input type="number" min="0" step="1" value={freeBaseMonths} onChange={(e) => setFreeBaseMonths(Number(e.target.value))} className="w-full bg-transparent px-4 py-4 text-[18px] outline-none" />
                  <span className="px-4 font-mono text-[13px] text-annot">months</span>
                </div>
              </label>
            </div>

            <div className="mt-8 border-l-2 border-redline bg-redline-100/40 px-5 py-4">
              <p className="font-mono text-[10px] uppercase tracking-wider2 text-redline-600">How this model treats free rent</p>
              <p className="mt-2 text-[14px] leading-relaxed text-graphite-600">
                Free-rent months reduce base rent only. TMI/additional rent remains payable in the model for every month. Actual lease wording controls, so change the assumptions to match the proposal you are reviewing.
              </p>
            </div>
          </div>

          <div>
            <DimensionLine label="Occupancy cost" className="mb-8" />
            {result ? (
              <div className="border border-medline bg-table">
                <div className="border-b border-hairline px-6 py-7">
                  <p className="annot">Monthly occupancy before HST, utilities & tenant-specific costs</p>
                  <p className="mt-2 font-display text-[42px] leading-none sm:text-[52px]">{money(result.monthlyOccupancy)}</p>
                </div>

                <div className="grid gap-px bg-medline sm:grid-cols-2">
                  <div className="bg-sheet px-6 py-6">
                    <p className="annot">Monthly base rent</p>
                    <p className="mt-2 font-display text-[25px]">{money(result.monthlyBase)}</p>
                  </div>
                  <div className="bg-sheet px-6 py-6">
                    <p className="annot">Monthly TMI / additional</p>
                    <p className="mt-2 font-display text-[25px]">{money(result.monthlyAdditional)}</p>
                  </div>
                  <div className="bg-sheet px-6 py-6">
                    <p className="annot">Annual occupancy</p>
                    <p className="mt-2 font-display text-[25px]">{money(result.annualOccupancy)}</p>
                  </div>
                  <div className="bg-sheet px-6 py-6">
                    <p className="annot">Effective average after {result.freeMonthsApplied} free base months</p>
                    <p className="mt-2 font-display text-[25px]">{money(result.effectiveMonthly)}/mo</p>
                  </div>
                </div>

                <div className="border-t border-hairline px-6 py-6">
                  <div className="flex flex-wrap items-end justify-between gap-5">
                    <div>
                      <p className="annot">Estimated term occupancy cost</p>
                      <p className="mt-2 font-display text-[30px]">{money(result.totalTerm)}</p>
                    </div>
                    <div className="text-right">
                      <p className="annot">Effective blended rent</p>
                      <p className="mt-2 font-display text-[23px]">${result.effectivePerSfYear.toFixed(2)}/sf/yr</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border border-medline bg-table px-6 py-8 text-[15px] text-annot">Enter valid lease assumptions.</div>
            )}

            <p className="mt-5 text-[13px] leading-relaxed text-annot">
              This is lease arithmetic, not a quote of market rent or legal interpretation of a lease. TMI definitions, gross-up clauses, utilities, percentage rent, management fees, taxes, insurance, capital recoveries and HST can differ by property and document.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact?interest=leasing" className="btn-primary">Review a lease or listing with Pinnacle Commercial</Link>
              <Link to="/lease-my-commercial-property-waterloo-region-guelph" className="btn-ghost">I have space to lease</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-medline">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="Terms people mix up" className="mb-9" />
          <div className="grid gap-8 md:grid-cols-3">
            <div>
              <h2 className="font-display text-[20px]">Net / base rent</h2>
              <p className="mt-3 text-[14px] leading-relaxed text-annot">The quoted rent for the space itself, usually expressed annually per rentable square foot.</p>
            </div>
            <div>
              <h2 className="font-display text-[20px]">TMI / additional rent</h2>
              <p className="mt-3 text-[14px] leading-relaxed text-annot">A separate estimate or charge for items allocated under the lease, commonly including some combination of taxes, maintenance and insurance.</p>
            </div>
            <div>
              <h2 className="font-display text-[20px]">Effective rent</h2>
              <p className="mt-3 text-[14px] leading-relaxed text-annot">A way to spread modeled concessions such as free base rent across the full term. It is useful for comparing proposals, but it does not replace the actual payment schedule.</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
